import styled from "@emotion/styled"
import { useState, useEffect } from "react"
import { useConsole } from "contexts/Console"
import { motion, useMotionValueEvent } from "framer-motion"
import getMediaQuery from "css/breakpoints"
import { useMoonCalendar } from "./context"
import { Icon } from "components/icon/Icon"
import * as button from "css/buttons"
import { ButtonStyled } from "components/button/Button"
import MoonphaseCalendarPopin from "./MoonphaseCalendarPopin"
import { useMoonphaseCalendar } from "contexts/MoonphaseCalendar"
import { headline50, surtitle70, legend100, body40, bold } from "css/text"
import { convertToIndianNumerals } from "./context"
import * as lunar from "./lunar"

const Main = styled(motion.div)`
  position: relative;
  z-index: 1;
  grid-row: 1;
  grid-column: main;
  display: grid;
  justify-items: center;
  text-align: center;
  padding-block: 5rem 1.75rem;
  opacity: 0;

  @property --mc-kicker-y {
    syntax: "<percentage>";
    inherits: true;
    initial-value: 100%;
  }

  @property --mc-title-y {
    syntax: "<percentage>";
    inherits: true;
    initial-value: 100%;
  }

  @property --mc-cta-alpha {
    syntax: "<number>";
    inherits: true;
    initial-value: 0;
  }

  div.mc-overflow {
    overflow: hidden;
  }

  .mc-loaded & {
    opacity: 1;
    --mc-title-y: 0%;
    --mc-kicker-y: 0%;
    --mc-cta-alpha: 1;
  }

  ${getMediaQuery("s")} {
    grid-column: 2 / span 6;
    padding-block: 1.75rem;
  }
  ${getMediaQuery("m")} {
    grid-column: 4 / span 8;
  }
`

const Kicker = styled.h1`
  position: relative;
  ${surtitle70}
  display: block;
  transform: translateY(var(--mc-kicker-y));
  transition: transform 2s cubic-bezier(0.22, 1, 0.36, 1) 1.8s;

  html.prefers-reduced-motion & {
    transition-duration: 0s;
    transition-delay: 0s;
  }

  html.prefers-contrast & {
    color: black !important;

    ::after {
      background: white;
      content: "";
      height: calc(100% + 0px);
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 0px);
      z-index: -1;
    }
  }

  padding-block-end: 10px;
  margin-block-end: 0;
`

const Title = styled.h2`
  position: relative;
  ${headline50}
  min-height: 2lh;
  display: grid;
  align-items: start;

  transform: translateY(var(--mc-title-y));
  transition: transform 2s cubic-bezier(0.22, 1, 0.36, 1) 2s;

  html.prefers-reduced-motion & {
    transition-duration: 0s;
    transition-delay: 0s;
  }

  html.prefers-contrast & {
    color: black !important;

    ::after {
      background: white;
      content: "";
      height: calc(100% + 0px);
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 0px);
      z-index: -1;
    }
  }

  span {
    display: block;
  }

  &.mc-nav-title-fade-in {
    animation: navTitleFadeIn 2s cubic-bezier(0.33, 1, 0.68, 1) forwards;

    html.prefers-reduced-motion & {
      animation: none;
    }
  }

  @keyframes navTitleFadeIn {
    from {
      opacity: 0;
    }
    25% {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  ${getMediaQuery("m")} {
    min-height: unset;
  }
`
const DateWrapper = styled.div`
  position: relative;
  ${body40}
  margin-block: 20px 10px;
  opacity: var(--mc-cta-alpha);
  transition: opacity 2s cubic-bezier(1, 1, 1, 1) 2s;

  html.prefers-reduced-motion & {
    transition-duration: 0s;
    transition-delay: 0s;
  }
`

const Date = styled(ButtonStyled)`
  background: var(--container, var(--grey));
  border-radius: 9999px;
  height: var(--height);
  padding-inline: var(--padding);

  --circradius: 34px;

  ${getMediaQuery("m")} {
    --circradius: 38px;
  }

  &::before {
    content: "";
    width: var(--circradius);
    height: var(--circradius);
    background: white;
    border-radius: 50%;
    inset-inline-start: 3px;
    inset-block-start: 3px;
    position: absolute;
  }

  position: relative;
  ${bold}
  cursor: pointer;
  pointer-events: auto;
  min-width: 10rem !important;

  svg {
    width: 16px !important;
    height: 16px !important;
    z-index: 1;
    fill: #212121;
    margin-inline-start: -1.125rem !important;
    margin-inline-end: 1.125rem !important;

    ${getMediaQuery("m")} {
      margin-inline-start: -1.25rem !important;
      margin-inline-end: 1.25rem !important;
    }
  }

  span {
    clip: unset !important;
    width: unset !important;
    height: unset !important;
    position: relative !important;
    clip-path: unset !important;
    display: block !important;
    min-width: 11rem;

    &:lang(ar),
    &:lang(fa) {
      min-width: 12rem;
    }
  }

  ${getMediaQuery("m")} {
    min-width: 16rem;
  }
`

const Legend = styled.span`
  position: relative;
  display: block;
  ${legend100}
  opacity: var(--mc-cta-alpha);
  transition: opacity 2s cubic-bezier(1, 1, 1, 1) 2s;

  html.prefers-reduced-motion & {
    transition-duration: 0s;
    transition-delay: 0s;
  }

  html.prefers-contrast & {
    color: black !important;

    ::after {
      background: white;
      content: "";
      height: calc(100% + 10px);
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 10px);
      z-index: -1;
    }
  }

  ${getMediaQuery("m")} {
    max-width: 60%;
  }
`

function formatDate(tm, locale, timezone) {
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    ...(locale === "zh-hant" && { hour12: false }),
    //    second: '2-digit',
    ...(timezone !== "local" && { timeZone: timezone }),
  }).format(tm)
}

function MCDate(props) {
  const console = useConsole()
  const { ctx, heading, labels, timezone, sprite_cal } = props
  const { isCalendarOpen, openCalendar } = useMoonphaseCalendar()
  const { moon_time, moving, locale } = useMoonCalendar(ctx)
  const [label, setLabel] = useState(printDateAR(formatDate(moon_time.get(), locale, timezone)))

  function printDateAR(d) {
    return convertToIndianNumerals(d, "ar", locale)
  }

  useMotionValueEvent(moon_time, "change", v => {
    const s = formatDate(v, locale, timezone)
    setLabel(printDateAR(s))
  })

  useEffect(() => {
    const s = formatDate(moon_time.get(), locale, timezone)
    setLabel(printDateAR(s))
  }, [locale, moon_time, timezone])

  function selectCalendarDate(date, fm = false) {
    // Default fallback to 21:00 for standard calendar days
    let targetTime = date.set({ hour: 21, minute: 0, second: 0, millisecond: 0 }).toMillis()

    if (fm) {
      // 1. Convert your calendar date object (Luxon) to a native JavaScript Date instance
      const jsDate = date.toJSDate()

      // 2. Fetch the exact past and future full moon instants using your library
      const prevFM = lunar.lastFullMoonBefore(jsDate)
      const nextFM = lunar.nextFullMoonAfter(jsDate)

      // 3. Find which of the two full moon instants is closer to the selected calendar day
      const diffPrev = Math.abs(jsDate.getTime() - prevFM.getTime())
      const diffNext = Math.abs(jsDate.getTime() - nextFM.getTime())

      const closestFullMoon = diffPrev < diffNext ? prevFM : nextFM

      // 4. Extract the exact millisecond timestamp matching your mathematical model
      targetTime = closestFullMoon.getTime()
    }

    moving.set(targetTime)
  }

  function selectCalendarDateOld(date, fm = false) {
    const targetTime = date.set({ hour: 21, minute: 0, second: 0, millisecond: 0 }).toMillis()
    moving.set(targetTime)
  }

  return (
    <>
      <DateWrapper>
        <Date
          aria-expanded={isCalendarOpen}
          aria-haspopup='dialog'
          aria-label={labels?.open_calendar ?? "Open the calendar"}
          icon='none'
          className='filled translucent-light'
          onClick={openCalendar}
        >
          <Icon type='calendar' size={16} viewBox='0 0 20.8333 20.8333' />
          <span>{label}</span>
          <p role='status' className='sr-only' aria-atomic='true'>
            {label}
          </p>
        </Date>
      </DateWrapper>
      <MoonphaseCalendarPopin
        main_title={heading?.title}
        labels={labels}
        locale={locale}
        onSelectDate={selectCalendarDate}
        selectedTime={moon_time}
        timezone={timezone}
        sprite_cal={sprite_cal}
        ctx={ctx}
      />
    </>
  )
}

function MCTitle(props) {
  const console = useConsole()
  const { ctx, labels } = props
  const { moon_phase_label } = useMoonCalendar(ctx)
  const [label, setLabel] = useState(updateLabel(moon_phase_label.get()))

  function updateLabel(v) {
    return labels?.[v]
  }

  function onMoonPhaseLabel(v) {
    setLabel(updateLabel(v))
  }
  useMotionValueEvent(moon_phase_label, "change", onMoonPhaseLabel)

  return (
    <Title key={label} className='mc-nav-title-fade-in'>
      <span role='status'>{label}</span>
    </Title>
  )
}

function MCLegend(props) {
  const { ctx, labels } = props
  const { focused } = useMoonCalendar(ctx)
  const [keyb, setKeyb] = useState(0)

  function onFocused(v) {
    setKeyb(v)
  }
  useMotionValueEvent(focused, "change", onFocused)

  return <Legend>{labels[keyb ? "slide_the_dates_accessibility" : "slide_the_dates"]}</Legend>
}

export default function MoonCalendarDate(props) {
  const console = useConsole()
  const { heading, labels } = props

  return (
    <Main className='dark-theme'>
      <div className='mc-overflow'>
        <Kicker>{heading.title}</Kicker>
      </div>
      <div className='mc-overflow'>
        <MCTitle {...props} />
      </div>
      <MCDate {...props} />
      <MCLegend {...props} />
    </Main>
  )
}
